import React, { useState, useMemo, useEffect } from "react";
import "../fleet/FleetManagementView.css";
import "../../operation/battery/OperationView.css";
import "../PremiumGrid.css";
import { Driver, Depot, Vehicle, db, depots, licenseTypes } from "../../data/database";

interface DriverManagementProps {
  drivers: Driver[];
  setDrivers: React.Dispatch<React.SetStateAction<Driver[]>>;
  vehicles: Vehicle[];
  setVehicles: React.Dispatch<React.SetStateAction<Vehicle[]>>;
}

export default function DriverManagementView({ drivers, setDrivers, vehicles, setVehicles }: DriverManagementProps) {
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [formDriver, setFormDriver] = useState<Driver | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<{ key: keyof Driver; direction: 'ascending' | 'descending'; } | null>(
    { key: 'name', direction: 'ascending' }
  );

  useEffect(() => {
    setFormDriver(selectedDriver);
    setIsEditing(false);
  }, [selectedDriver]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, rowsPerPage]);

  const filteredDrivers = useMemo(() => {
    let filtered = [...drivers];
    if (searchQuery.trim()) {
      filtered = filtered.filter(d =>
        d.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (sortConfig) {
      filtered.sort((a, b) => {
        const va = a[sortConfig.key] || "";
        const vb = b[sortConfig.key] || "";
        if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [drivers, sortConfig, searchQuery]);

  const pageCount = Math.max(1, Math.ceil(filteredDrivers.length / rowsPerPage));
  const startIndex = page * rowsPerPage;
  const pageRows = filteredDrivers.slice(startIndex, startIndex + rowsPerPage);

  const requestSort = (key: keyof Driver) => {
    setSortConfig(prev => ({
      key,
      direction: prev && prev.key === key && prev.direction === "ascending"
        ? "descending"
        : "ascending",
    }));
  };

  const handleNewClick = () => {
    const newId = `DRV-${Math.max(0, ...drivers.map(d => parseInt(d.id.split('-')[1]) || 0)) + 1}`;
    const newDriver: Driver = {
      id: newId,
      name: "",
      depot: "",
      contact: "",
      email: "ilyang@ilyang.com",
      licenseType: "1종 보통",
      isEmployed: true,
    };
    setSelectedDriver(null);
    setFormDriver(newDriver);
    setIsNew(true);
    setIsEditing(true);
  };

  const handleEditClick = () => {
    if (selectedDriver) setIsEditing(true);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(pageRows.map((d) => d.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (window.confirm(`선택한 ${selectedIds.length}명의 운전자를 삭제하시겠습니까?`)) {
      try {
        for (const id of selectedIds) {
          await db.deleteDriver(id);
        }
        const updatedDrivers = await db.fetchDrivers();
        setDrivers(updatedDrivers);
        setSelectedIds([]);
        alert("삭제되었습니다.");
      } catch (e: any) {
        alert("삭제 실패: " + e.message);
      }
    }
  };

  const handleSave = async () => {
    if (!formDriver) return;

    const { name, contact, email, depot, isEmployed } = formDriver;

    if (!name.trim()) return alert("운전자 이름을 입력해주세요.");
    if (!contact.trim()) return alert("연락처를 입력해주세요.");
    if (!email.trim()) return alert("이메일을 입력해주세요.");
    if (!depot) return alert("지점을 선택해주세요.");
    if (isEmployed === undefined) return alert("고용 여부를 선택해주세요.");

    setIsSaving(true);
    try {
      const driverToSave = { ...formDriver, name: name.trim() };

      if (isNew) {
        await db.addDriver(driverToSave);
      } else {
        await db.updateDriver(driverToSave);
      }

      const updatedDrivers = await db.fetchDrivers();
      setDrivers(updatedDrivers);

      alert("저장되었습니다.");
      setSelectedDriver(driverToSave);
      setIsEditing(false);
      setIsNew(false);
    } catch (e: any) {
      alert("저장 실패: " + e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDriver) return;

    if (!window.confirm(`${selectedDriver.name} 운전자 정보를 삭제하시겠습니까?`)) return;

    try {
      await db.deleteDriver(selectedDriver.id);

      const updatedDrivers = await db.fetchDrivers();
      setDrivers(updatedDrivers);

      const vehicleToUpdate = vehicles.find(v => v.assignee === selectedDriver.name);
      if (vehicleToUpdate) {
        await db.updateVehicle({ ...vehicleToUpdate, assignee: undefined });
        const updatedVehicles = await db.fetchVehicles();
        setVehicles(updatedVehicles);
      }

      setSelectedDriver(null);
      setFormDriver(null);
      setIsEditing(false);
      setIsNew(false);
      alert("삭제되었습니다.");
    } catch (e: any) {
      alert("삭제 실패: " + e.message);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      setFormDriver(null);
      setSelectedDriver(null);
      setIsNew(false);
    } else {
      setFormDriver(selectedDriver);
    }
    setIsEditing(false);
  };

  const handleCloseDrawer = () => {
    setSelectedDriver(null);
    setFormDriver(null);
    setIsEditing(false);
    setIsNew(false);
  };

  const handleFormChange = (field: keyof Driver, value: string | boolean) => {
    if (formDriver) {
      setFormDriver({ ...formDriver, [field]: value });
    }
  };

  const isDrawerOpen = Boolean(selectedDriver || isNew);

  return (
    <div className="vm-page">
      <div className="soh-header-left" style={{ marginBottom: '24px' }}>
        <h2 className="soh-header-title">운전자 관리</h2>
        <div className="soh-header-sub">운전자 정보의 등록, 수정 및 배차 현황을 관리합니다.</div>
      </div>
      {/* 통합 툴바 */}
      <div className="driving-toolbar" style={{ marginTop: '0', border: '1px solid #e2e8f0', borderBottom: 'none', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
        <div className="driving-toolbar-left" style={{ gap: '16px' }}>
          <div className="driving-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            운전자 정보
          </div>
        </div>

        <div className="driving-toolbar-right">
          <button className="driving-action-btn primary-outline" onClick={() => window.location.reload()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
            새로고침
          </button>

          <button className="driving-action-btn green-outline" onClick={() => alert("데이터 내보내기 기능은 추후 연동 예정입니다.")}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="8" y1="13" x2="16" y2="13"></line>
              <line x1="8" y1="17" x2="16" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            데이터 내보내기
          </button>


          
          <button
            className="driving-action-btn red-outline"
            disabled={selectedIds.length === 0}
            onClick={handleBulkDelete}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
            삭제
          </button>

          <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 4px' }}></div>

          <button className="driving-action-btn blue-solid" onClick={handleNewClick}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
            </svg>
            사용자 등록
          </button>

          <div className="driving-search-wrapper">
            <input
              type="text"
              placeholder="이름, 아이디 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="driving-search-input"
            />
            <svg className="driving-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
        </div>
      </div>

      {/* 메인 레이아웃 */}
      <div className="vm-main">
        {/* 목록 테이블 */}
        <div className="soh-table-card">

          <div className="soh-table-wrapper" style={{ overflowX: 'auto' }}>
            <table className="soh-table premium-grid driver-grid" style={{ minWidth: '850px' }}>
              <thead>
                <tr>
                  <th className="w-chk">
                    <input
                      type="checkbox"
                      className="grid-chk"
                      checked={
                        pageRows.length > 0 &&
                        selectedIds.length === pageRows.length &&
                        pageRows.every((r) => selectedIds.includes(r.id))
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th onClick={() => requestSort("name")}>운전자 이름 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("contact")}>연락처 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("email")}>이메일 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("licenseType")}>면허 종류 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("isEmployed")}>고용 형태 <span className="sort-icon">▼</span></th>
                  <th onClick={() => requestSort("assignedVehicle")}>운행차량 <span className="sort-icon">▼</span></th>
                </tr>
              </thead>

              <tbody>
                {pageRows.map((driver) => (
                  <tr
                    key={driver.id}
                    className={selectedDriver?.id === driver.id ? "soh-row-selected" : ""}
                    onClick={() => {
                      setSelectedDriver(driver);
                      setIsEditing(true);
                    }}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="grid-chk"
                        checked={selectedIds.includes(driver.id)}
                        onChange={() => handleSelectOne(driver.id)}
                      />
                    </td>
                    <td>
                      <div className="grid-icon-text">
                        <div className="grid-avatar">{driver.name.charAt(0)}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>{driver.name}</span>
                          {driver.depot && (
                            <span style={{ fontSize: '11px', color: '#0284c7', backgroundColor: '#e0f2fe', padding: '1px 4px', borderRadius: '3px', fontWeight: 700 }}>{driver.depot}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="grid-text">{driver.contact}</td>
                    <td className="grid-text">{driver.email}</td>
                    <td>
                      <span className={`license-tag ${driver.licenseType.includes("대형") || driver.licenseType.includes("특수") ? "special" :
                        driver.licenseType.includes("1종") ? "large" : "normal"
                        }`}>
                        {driver.licenseType}
                      </span>
                    </td>
                    <td>
                      {driver.isEmployed ? (
                        <div className="grid-badge grid-badge-success">
                          직영 근무자
                        </div>
                      ) : (
                        <div className="grid-badge grid-badge-neutral">
                          개인 사업자
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="grid-vehicle">
                        {vehicles.find(v => v.assignee === driver.name)?.vehicleNumber || "미지정"}
                      </div>
                    </td>
                  </tr>
                ))}

                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="soh-empty">조건에 맞는 운전자가 없습니다.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          <div className="soh-pagination-advanced">
            <div className="pagination-controls">
              <button className="pag-btn pag-nav" disabled={page === 0} onClick={() => setPage(0)}>|&lt;</button>
              <button className="pag-btn pag-nav" disabled={page === 0} onClick={() => setPage(p => p - 1)}>&lt;</button>

              {/* Simple page numbers logic based on current page */}
              {Array.from({ length: Math.min(10, pageCount) }, (_, i) => {
                let startPage = Math.max(0, Math.min(page - 4, pageCount - 10));
                let pageNum = startPage + i;
                if (pageNum >= pageCount) return null;
                return (
                  <button
                    key={pageNum}
                    className={`pag-btn ${page === pageNum ? 'active' : ''}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}

              <button className="pag-btn pag-nav" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>&gt;</button>
              <button className="pag-btn pag-nav" disabled={page >= pageCount - 1} onClick={() => setPage(pageCount - 1)}>&gt;|</button>
            </div>

            <div className="pagination-info">
              <span>{page + 1}/{pageCount} 페이지({filteredDrivers.length} 개)</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>페이지 당 행 수:</span>
                <select className="soh-select" style={{ padding: '4px 8px' }} value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* 오른쪽 상세 패널 오버레이 (Drawer) */}
        <div className={`vm-backdrop ${isDrawerOpen ? "open" : ""}`} onClick={handleCloseDrawer}></div>
        <aside className={`vm-form-container ${isDrawerOpen ? "open" : ""}`}>
          {formDriver ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <h3 className="vm-form-title" style={{ margin: 0 }}>
                  {isNew ? "신규 운전자 등록" : isEditing ? "운전자 정보 수정" : "운전자 정보 조회"}
                </h3>
                <button
                  onClick={handleCloseDrawer}
                  style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
                >
                  &times;
                </button>
              </div>

              <div className="vm-form-field">
                <label>운전자 이름<span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={formDriver.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                  placeholder="운전자 이름 입력"
                  readOnly={!isNew}
                  style={!isNew ? { backgroundColor: '#f8fafc', color: '#94a3b8' } : {}}
                />
              </div>

              <div className="vm-form-field">
                <label>연락처</label>
                <input
                  type="text"
                  value={formDriver.contact}
                  onChange={(e) => handleFormChange("contact", e.target.value)}
                  placeholder="010-1234-5678"
                />
              </div>

              <div className="vm-form-field">
                <label>이메일</label>
                <input
                  type="email"
                  value={formDriver.email}
                  onChange={(e) => handleFormChange("email", e.target.value)}
                  placeholder="ilyang@ilyang.com"
                />
              </div>

              <div className="vm-form-field">
                <label>지점</label>
                <select
                  value={formDriver.depot}
                  onChange={(e) => handleFormChange("depot", e.target.value)}
                >
                  <option value="" disabled>지점 선택</option>
                  {depots.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="vm-form-field">
                <label>면허 종류</label>
                <select
                  value={formDriver.licenseType}
                  onChange={(e) => handleFormChange("licenseType", e.target.value)}
                >
                  <option value="" disabled>면허 종류 선택</option>
                  {licenseTypes.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>

              <div className="vm-form-field">
                <label>사업자 등록번호</label>
                <input
                  type="text"
                  value={formDriver.businessRegistrationNumber || ""}
                  onChange={(e) => handleFormChange("businessRegistrationNumber", e.target.value)}
                  placeholder="사업자만 입력"
                />
              </div>

              <div className="dm-form-field-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                <label htmlFor="isEmployed" style={{ fontSize: '13px', fontWeight: 600, color: '#4b5563', cursor: 'pointer' }}>고용 여부</label>
                <input
                  id="isEmployed"
                  type="checkbox"
                  checked={formDriver.isEmployed}
                  onChange={(e) => handleFormChange("isEmployed", e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
              </div>

              <div className="vm-form-field">
                <label>운행차량</label>
                <input
                  type="text"
                  value={vehicles.find(v => v.assignee === formDriver.name)?.vehicleNumber || "미지정"}
                  readOnly
                  disabled
                  style={{ backgroundColor: '#f8fafc', color: '#94a3b8' }}
                />
              </div>

              <div className="vm-drawer-footer">
                <div className="drawer-footer-left">
                  {!isNew && (
                    <button className="drawer-btn-delete" onClick={handleDelete}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                      삭제
                    </button>
                  )}
                </div>
                <div className="drawer-footer-right">
                  <button className="drawer-btn-cancel" onClick={handleCancel}>
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    취소
                  </button>
                  <button
                    className="drawer-btn-save"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {isSaving ? "저장중..." : "저장하기"}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div >
  );
}
